import { extractCurrencyInfo } from "../../../../../common/helpers/currency";
import { decodeHtmlEntities } from "../../../../../common/helpers/decodeHtmlEntities";
import type { RawCart } from "../../../../../types";
import type { TransformedCart } from "../../types/cartApiTypes";
import {
  transformAddress,
  transformCartItem,
  transformCartTotals,
  transformShippingRates,
  transformCoupons,
  transformFreeShippingData,
} from "./cartDataTransformationHelpers";

// Define clear types for the dependencies we will pass in
interface LanguageInfo {
  currentLanguage: string;
  shouldTranslate: boolean;
}

/**
 * Transforms the entire raw cart object from the API by orchestrating smaller helper functions.
 * @param cart - The raw cart object from the WC Store API.
 * @param languageInfo - An object containing language context for translations.
 * @returns A transformed cart object ready for the UI.
 */
export const transformCart = (
  cart: RawCart,
  languageInfo: LanguageInfo,
): TransformedCart => {
  // Extract currency info once from the main totals object.
  const currencyInfo = extractCurrencyInfo(cart.totals);

  // --- Call the helper to handle free shipping data ---
  const freeShippingData = transformFreeShippingData(cart);

  // --- Extract Additional Billing Info Extension ---
  const additionalBillingInfo = cart.extensions?.additional_billing_info;
  const billingExtras = additionalBillingInfo
    ? {
        customerType: additionalBillingInfo.customer_type,
        tckn: additionalBillingInfo.tckn,
        vkn: additionalBillingInfo.vkn,
      }
    : undefined;

  // --- DELIVERY ESTIMATION MAPPING ---
  const deliveryRaw = cart.extensions?.delivery_estimation;
  const deliveryEstimation = deliveryRaw
    ? {
        minDate: deliveryRaw.min_date,
        maxDate: deliveryRaw.max_date,
        minDays: deliveryRaw.min_days,
        maxDays: deliveryRaw.max_days,
        displayString: deliveryRaw.display_string,
      }
    : undefined;

  // --- EXTRACT FIRST PURCHASE PROMOTION ---
  const rawFirstPurchase = cart.extensions?.first_purchase_promotion;
  const firstPurchasePromotion = rawFirstPurchase
    ? {
        isActive: rawFirstPurchase.is_active,
        percentage: rawFirstPurchase.percentage,
        isUserEligible: rawFirstPurchase.is_user_eligible,
        isGuest: rawFirstPurchase.is_guest,
        promoText: rawFirstPurchase.promo_text,
      }
    : undefined;

  // --- EXTRACT APPOINTMENT ---
  const rawAppointment = cart.extensions?.appointment as any;
  let needsAppointment = false;
  if (rawAppointment) {
    needsAppointment =
      typeof rawAppointment === "object"
        ? !!rawAppointment.needs_appointment
        : !!rawAppointment;
  }
  const appointment = { needsAppointment };

  // --- EXTRACT SPEND AND SAVE PROMOTION ---
  const rawSpendAndSave = cart.extensions?.spend_and_save_promotion;
  const spendAndSavePromotion = rawSpendAndSave
    ? {
        isActive: rawSpendAndSave.is_active,
        threshold: rawSpendAndSave.threshold,
        currentSubtotal: rawSpendAndSave.current_subtotal,
        multiplier: rawSpendAndSave.multiplier,
        earnedDiscount: rawSpendAndSave.earned_discount,
        nextDiscount: rawSpendAndSave.next_discount,
        amountNeeded: rawSpendAndSave.amount_needed,
        promoTextInitial: rawSpendAndSave.promo_text_initial,
        promoTextProgress: rawSpendAndSave.promo_text_progress,
      }
    : undefined;

  // --- SYSTEM-WIDE VALIDATION ERROR DECODING ---
  const decodedErrors = (cart.errors || []).map((err) => ({
    code: err.code,
    message: decodeHtmlEntities(err.message),
  }));

  // --- Assemble the final transformed cart object ---
  return {
    currencyCode: cart.totals.currency_code,
    items: cart.items.map((item) => transformCartItem(item, languageInfo)),
    itemsCount: cart.items_count,
    itemsWeight: cart.items_weight,
    totals: transformCartTotals(cart.totals, cart.shipping_rates),
    coupons: transformCoupons(cart.coupons),
    fees: cart.fees,
    shippingAddress: transformAddress(cart.shipping_address),
    billingAddress: transformAddress(cart.billing_address, billingExtras),
    needsShipping: cart.needs_shipping,
    hasCalculatedShipping: cart.has_calculated_shipping,
    shippingRates: transformShippingRates(cart.shipping_rates, currencyInfo),
    ...freeShippingData,
    needsPayment: cart.needs_payment,
    errors: decodedErrors,
    crossSellIds: cart.cross_sells,
    deliveryEstimation: deliveryEstimation,
    firstPurchasePromotion: firstPurchasePromotion,
    spendAndSavePromotion: spendAndSavePromotion,
    appointment: appointment,
  };
};
